import { Images } from "lucide-react"
import { useMemo, useRef, useState, type DragEvent, type ReactNode } from "react"
import { toast } from "sonner"
import {
  addShotReferenceInput,
  addShotReferenceInputFromSource,
  removeShotReferenceInput,
  type ShotReferenceInputKind,
  type ShotReferenceInputRow,
} from "@/lib/shot-reference-inputs-api"
import { ImageLightboxDialog } from "@/components/image-lightbox-dialog"
import { LibraryPickerDialog } from "@/components/library/library-picker-dialog"
import { SketchDialog } from "@/components/sketch/sketch-dialog"
import { AddDashedTile } from "@/components/ui/add-dashed-tile"
import { ReferenceInputThumb } from "@/components/reference-inputs/reference-input-thumb"
import { useMultiFileUpload } from "@/hooks/use-multi-file-upload"
import { usePasteFiles } from "@/hooks/use-paste-files"
import { useShotPanelWorkspace } from "@/hooks/use-shot-panel-workspace"
import { useTranslation } from "@/hooks/use-translation"
import { isFileDrag } from "@/lib/filter-files-by-accept"
import {
  filterReferenceFilesByMode,
  isAllowedReferenceAudioFile,
  isAllowedReferenceImageFile,
  isAllowedReferenceMediaFile,
  isAllowedReferenceVideoFile,
  isReferenceFileWithinSize,
  readVideoDurationSeconds,
  REFERENCE_IMAGE_FILE_ACCEPT,
  REFERENCE_MEDIA_FILE_ACCEPT,
  REFERENCE_VIDEO_MIN_SECONDS,
  referenceMaxUploadMb,
} from "@/lib/reference-file-policy"
import {
  librarySourceKey,
  projectLibraryUploadSource,
  projectUploadSource,
  shotReferenceInputSource,
} from "@/lib/library-source"
import { promptMentionsReference } from "@/lib/reference-label"
import { cn } from "@/lib/utils"
import { confirm } from "@/lib/confirm"
import type { LibraryPickResult } from "@/hooks/use-library-pick-selection"
import {
  maxTotalVideoRefSecondsForProject,
  maxVideoRefSecondsForProject,
  roundedVideoRefSeconds,
  sumVideoRefSeconds,
  wouldExceedVideoRefTotal,
} from "@/lib/video-duration"

type Props = {
  projectId: number
  shotId: number
  kind: ShotReferenceInputKind
  items: ShotReferenceInputRow[]
  /** Текущие промпты вкладки — если содержат label референса, при удалении покажем предупреждение */
  promptWatchText: string
  onChanged?: () => void
  className?: string
  /** Разрешить загрузку видео и аудио в дополнение к изображениям */
  acceptVideo?: boolean
  /** Клик по лейблу референса — вставить его в текст промпта */
  onInsertLabel?: (label: string) => void
  /** Элемент, отображаемый первым в ряду референсов (например, опорный кадр) */
  leadingItem?: ReactNode
}

function thumbMediaType(mediaType: ShotReferenceInputRow["media_type"]): "image" | "video" | "audio" | "voice" {
  if (mediaType === "video") return "video"
  if (mediaType === "audio") return "audio"
  if (mediaType === "voice") return "voice"
  return "image"
}

function uploadKindForFile(
  file: File,
  panelKind: ShotReferenceInputKind,
  acceptVideo: boolean,
): ShotReferenceInputKind {
  if (acceptVideo && isAllowedReferenceAudioFile(file)) return "audio"
  return panelKind
}

export function ShotReferenceInputsPanel({
  projectId,
  shotId,
  kind,
  items,
  promptWatchText,
  onChanged,
  className,
  acceptVideo = false,
  onInsertLabel,
  leadingItem,
}: Props) {
  const { t } = useTranslation(["shot", "common"])
  const { project, project_permissions, generation_models_by_context } = useShotPanelWorkspace()
  const [removing, setRemoving] = useState<number | null>(null)
  const [previewItem, setPreviewItem] = useState<ShotReferenceInputRow | null>(null)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [addingFromLibrary, setAddingFromLibrary] = useState(false)
  const [fileDragOver, setFileDragOver] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Уже стоящее в этой панели гасим в пикере: сама строка видна в папке шота как
  // shot_reference_input, а прикреплённая ссылкой загрузка узнаётся по id блоба —
  // ключа источника у неё нет, metadata блоба принадлежит владельцу файла. Загрузок
  // две разновидности, и у обеих entity_id — тот же blob_id: доски Холста
  // (project_upload) и секции «Загрузки» проекта (project_library_upload).
  const pickedKeys = useMemo(
    () =>
      items.flatMap((row) => {
        const keys = [librarySourceKey(shotReferenceInputSource(row.id))]
        if (row.blob_id != null) {
          keys.push(librarySourceKey(projectUploadSource(row.blob_id)))
          keys.push(librarySourceKey(projectLibraryUploadSource(row.blob_id)))
        }
        return keys
      }),
    [items],
  )

  const canEditContent = project_permissions?.can_edit_content === true
  const shotVideoModels = generation_models_by_context?.shot_video
  const videoRefMaxSeconds = project ? maxVideoRefSecondsForProject(project, shotVideoModels) : 15
  const videoRefTotalMaxSeconds = project ? maxTotalVideoRefSecondsForProject(project, shotVideoModels) : 15
  const existingVideoRefSeconds = useMemo(() => sumVideoRefSeconds(items), [items])
  // JSON-ветка reference_inputs принимает только storyboard|video — на других панелях плитку не показываем.
  const libraryKind = kind === "storyboard" || kind === "video" ? kind : null

  const fileAccept = acceptVideo ? REFERENCE_MEDIA_FILE_ACCEPT : REFERENCE_IMAGE_FILE_ACCEPT
  const acceptMode = acceptVideo ? "media" : "image"

  const { fileRef, uploading, handleFiles, inputProps } = useMultiFileUpload({
    uploadOne: (file) => addShotReferenceInput(projectId, shotId, file, uploadKindForFile(file, kind, acceptVideo)),
    onBatchComplete: async () => {
      await onChanged?.()
    },
    onError: (e) => {
      console.error("shot reference upload:", e)
      toast.error(e instanceof Error ? e.message : t("referenceInputs.unsupportedFileType", { ns: "common" }))
    },
    accept: fileAccept,
    validateFile: async (file) => {
      if (!isReferenceFileWithinSize(file)) {
        throw new Error(t("referenceInputs.fileTooLarge", { ns: "common", max: referenceMaxUploadMb(file) }))
      }

      if (acceptVideo) {
        if (!isAllowedReferenceMediaFile(file)) {
          throw new Error(t("referenceInputs.unsupportedFileType", { ns: "common" }))
        }
      } else if (!isAllowedReferenceImageFile(file)) {
        throw new Error(t("referenceInputs.unsupportedFileType", { ns: "common" }))
      }

      if (isAllowedReferenceVideoFile(file)) {
        let duration: number
        try {
          duration = await readVideoDurationSeconds(file)
        } catch {
          throw new Error(t("referenceInputs.videoDurationUnreadable", { ns: "common" }))
        }
        if (duration < REFERENCE_VIDEO_MIN_SECONDS || duration > videoRefMaxSeconds) {
          throw new Error(
            t("referenceInputs.videoDurationInvalid", {
              ns: "common",
              min: REFERENCE_VIDEO_MIN_SECONDS,
              max: videoRefMaxSeconds,
            }),
          )
        }
        const total = existingVideoRefSeconds + duration
        if (wouldExceedVideoRefTotal(existingVideoRefSeconds, duration, videoRefTotalMaxSeconds)) {
          throw new Error(
            t("referenceInputs.videoDurationTotalInvalid", {
              ns: "common",
              max: videoRefTotalMaxSeconds,
              total: roundedVideoRefSeconds(total),
            }),
          )
        }
      }
      return true
    },
  })

  const rejectFiles = () => toast.error(t("referenceInputs.unsupportedFileType", { ns: "common" }))

  // scope: "page" — жест заказчика звучит как «пишу текст промпта, потом Ctrl+V»,
  // то есть вставка приходит из редактора рядом, а не из самой полосы плиток
  // (она вообще не фокусируема). Панель в композере смонтирована ровно одна:
  // вкладки «Кадр» и «Видео» взаимоисключающие.
  const { pasting } = usePasteFiles({
    targetRef: containerRef,
    onFiles: handleFiles,
    accept: acceptMode,
    scope: "page",
    enabled: canEditContent,
    onRejected: rejectFiles,
  })

  const busy = uploading || pasting

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!canEditContent || !isFileDrag(e.dataTransfer)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = "copy"
    setFileDragOver(true)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    setFileDragOver(false)
    if (!canEditContent || !isFileDrag(e.dataTransfer)) return
    e.preventDefault()
    const dropped = Array.from(e.dataTransfer.files)
    const batch = filterReferenceFilesByMode(dropped, acceptMode)
    if (batch.length === 0) {
      if (dropped.length > 0) rejectFiles()
      return
    }
    void handleFiles(batch)
  }

  // Загрузка файлов здесь давно пачкой (useMultiFileUpload), поэтому и выбор из
  // библиотеки набором: одиночный выбор был единственным местом, где за пять
  // референсов приходилось платить пятью открытиями модалки.
  const handlePickManyFromLibrary = async (picked: readonly LibraryPickResult[]) => {
    if (!libraryKind || addingFromLibrary || picked.length === 0) return
    setAddingFromLibrary(true)
    const failed: string[] = []
    let usedVideoSeconds = existingVideoRefSeconds
    try {
      // Строго последовательно: сервер считает position как MAX+1, и параллельные
      // запросы подрались бы за одну позицию.
      for (const entry of picked) {
        try {
          // Звук уходит под своим kind — ровно как uploadKindForFile при загрузке файла.
          // Под kind=video его не увидел бы StoryboardCustomReferences, который собирает
          // reference_audio_urls строго по kind: "audio".
          const rowKind = entry.media === "audio" ? "audio" : libraryKind
          if (entry.media === "video" && entry.durationMs != null) {
            const seconds = entry.durationMs / 1000
            if (seconds < REFERENCE_VIDEO_MIN_SECONDS || seconds > videoRefMaxSeconds) {
              throw new Error(
                t("referenceInputs.videoDurationInvalid", {
                  ns: "common",
                  min: REFERENCE_VIDEO_MIN_SECONDS,
                  max: videoRefMaxSeconds,
                }),
              )
            }
            if (wouldExceedVideoRefTotal(usedVideoSeconds, seconds, videoRefTotalMaxSeconds)) {
              throw new Error(
                t("referenceInputs.videoDurationTotalInvalid", {
                  ns: "common",
                  max: videoRefTotalMaxSeconds,
                  total: roundedVideoRefSeconds(usedVideoSeconds + seconds),
                }),
              )
            }
            usedVideoSeconds += seconds
          }
          await addShotReferenceInputFromSource(projectId, shotId, rowKind, entry.source)
        } catch (e) {
          console.error("shot reference from library:", e)
          // С причиной, а не одним именем: у аудио сервер отказывает по окну длительности
          // модели, и без текста ошибки повтор выглядит как поломка Библиотеки.
          const reason = e instanceof Error ? e.message.trim() : ""
          failed.push(reason ? `${entry.label} — ${reason}` : entry.label)
        }
      }
      // Пикер закрываем только когда прошло всё: иначе неудавшиеся исчезли бы молча.
      if (failed.length === 0) {
        setLibraryOpen(false)
      } else {
        toast.error(t("referenceInputs.addFromLibraryPartial", { names: failed.join(", ") }))
      }
      onChanged?.()
    } finally {
      setAddingFromLibrary(false)
    }
  }

  const handleRemove = async (row: ShotReferenceInputRow) => {
    const used = promptMentionsReference(promptWatchText, row.label)
    const msg = used
      ? t("referenceInputs.removeConfirmUsed", { label: row.label })
      : t("referenceInputs.removeConfirm", { label: row.label })
    if (!(await confirm({ message: msg, tone: "danger" }))) return
    setRemoving(row.id)
    try {
      await removeShotReferenceInput(projectId, shotId, row.id)
      onChanged?.()
    } catch (e) {
      console.error("shot reference remove:", e)
    } finally {
      setRemoving(null)
    }
  }

  const previewMedia = previewItem ? thumbMediaType(previewItem.media_type) : null
  const previewTitle =
    previewItem?.media_type === "voice" && previewItem.character_name
      ? `${previewItem.label} · ${previewItem.character_name}`
      : previewItem?.label
  const previewIsImage = previewMedia === "image" || previewMedia === "voice"
  const previewIsAudio = previewMedia === "audio"
  const previewIsVideo = previewMedia === "video"
  const previewIsSketch = previewIsImage || previewIsAudio

  if (!project) return null

  return (
    <div
      ref={containerRef}
      className={cn("space-y-2", className)}
      onDragOver={handleDragOver}
      onDragLeave={() => setFileDragOver(false)}
      onDrop={handleDrop}
      title={canEditContent ? t("referenceInputs.dropOrPasteHint", { ns: "common" }) : undefined}
    >
      <p className="text-[11px] font-medium tracking-wide text-ui-text-muted uppercase">{t("referenceInputs.title")}</p>
      <div
        className={cn("flex flex-wrap gap-2 rounded-lg", fileDragOver && "bg-ui-accent/10 ring-1 ring-ui-accent/30")}
      >
        {leadingItem}
        {items.map((item) => {
          const isVoice = item.media_type === "voice"
          return (
            <div key={item.id} className="flex flex-col gap-1">
              <ReferenceInputThumb
                url={isVoice ? (item.character_thumb_url ?? null) : item.url}
                thumbUrl={isVoice ? (item.character_thumb_url ?? null) : (item.thumb_url ?? null)}
                mediaType={thumbMediaType(item.media_type)}
                onOpenPreview={() => setPreviewItem(item)}
                onRemove={() => void handleRemove(item)}
                removing={removing === item.id}
                openPreviewTitle={
                  isVoice
                    ? t("referenceInputs.voiceReferenceTitle", { name: item.character_name ?? "" })
                    : t("referenceInputs.openPreview", { ns: "common" })
                }
                removeAriaLabel={t("referenceInputs.removeReference", { ns: "common" })}
              />
              <button
                type="button"
                title={t("referenceInputs.addToPrompt")}
                onClick={() => onInsertLabel?.(item.label)}
                className="max-w-[5.5rem] truncate rounded bg-ui-surface-raised px-1 py-0.5 text-center font-mono text-[10px] text-ui-accent-text hover:bg-ui-surface-raised"
              >
                {item.label}
              </button>
              {isVoice && item.character_name ? (
                <span className="max-w-[5.5rem] truncate text-center text-[10px] text-ui-text-muted">
                  {item.character_name}
                </span>
              ) : null}
            </div>
          )
        })}
        {/* Право спрашиваем и здесь: сервер всё равно откажет, а до этой правки
            участник с доступом «только чтение» узнавал об отказе уже после выбора файла. */}
        {canEditContent ? (
          <AddDashedTile
            onClick={() => fileRef.current?.click()}
            uploading={busy}
            ariaLabel={t("referenceInputs.addReference", { ns: "common" })}
          />
        ) : null}
        {canEditContent && libraryKind ? (
          <AddDashedTile
            onClick={() => setLibraryOpen(true)}
            uploading={addingFromLibrary}
            ariaLabel={t("referenceInputs.addFromLibrary")}
            title={t("referenceInputs.addFromLibrary")}
            icon={<Images className="h-5 w-5" aria-hidden />}
          />
        ) : null}
      </div>
      {acceptVideo && videoRefTotalMaxSeconds != null ? (
        <p className="mt-1.5 text-[11px] text-ui-text-muted">
          {t("referenceInputs.videoDurationBudget", {
            ns: "common",
            used: roundedVideoRefSeconds(existingVideoRefSeconds),
            max: videoRefTotalMaxSeconds,
          })}
        </p>
      ) : null}
      <input ref={fileRef} {...inputProps} />
      {libraryKind ? (
        <LibraryPickerDialog
          open={libraryOpen}
          onOpenChange={setLibraryOpen}
          projectId={projectId}
          frameFormat={project.frame_format}
          frameRate={project.effective_video_output_fps}
          initialShotId={shotId}
          pickedKeys={pickedKeys}
          subtitle={t("referenceInputs.addFromLibrary")}
          targets={[
            {
              id: libraryKind,
              // На кнопке — действие, а не описание источника: откуда выбирают,
              // человек и так видит по открытой Библиотеке.
              label: t("referenceInputs.attach"),
              // Панель «свои референсы шота» (kind=video) берёт картинку, видео и звук —
              // на сервере это media: :media. Панель опорника — только изображение.
              accepts: libraryKind === "video" ? (["image", "video", "audio"] as const) : (["image"] as const),
            },
          ]}
          busyTargetId={addingFromLibrary ? libraryKind : null}
          multiple
          onPick={() => undefined}
          onPickMany={(_targetId, picked) => void handlePickManyFromLibrary(picked)}
        />
      ) : null}
      <SketchDialog
        open={previewItem != null && previewIsSketch}
        onOpenChange={(open) => {
          if (!open) setPreviewItem(null)
        }}
        projectId={projectId}
        sourceUrl={
          previewMedia === "image"
            ? (previewItem?.download_url ?? previewItem?.url ?? null)
            : previewMedia === "voice"
              ? (previewItem?.character_thumb_url ?? null)
              : null
        }
        audioUrl={previewIsAudio ? (previewItem?.url ?? null) : null}
        presentation="view"
        allowEdit={false}
        persist="none"
        nested
        headerTitle={previewTitle}
      />
      <ImageLightboxDialog
        open={previewItem != null && previewIsVideo}
        onOpenChange={(open) => {
          if (!open) setPreviewItem(null)
        }}
        url={null}
        videoUrl={previewIsVideo ? (previewItem?.url ?? null) : null}
        title={previewTitle}
      />
    </div>
  )
}
